/**
 * Amplitude dev logger.
 *
 * Verifies that the official `data-amp-track-*` attributes (the ones Amplitude
 * autocapture actually reads) are present and follow the project naming
 * convention. Designed for manual QA / PM verification — every interaction
 * logs both the raw DOM attributes and the normalized event payload that
 * mimics Amplitude's `[Amplitude] Element Clicked` shape.
 *
 * See `docs/amplitude-element-tracking.md` for the conventions enforced here.
 */

import {
  AMP_TRACK_COMPONENT_ALIASES,
  AMP_TRACK_COMPONENT_DETAILS
} from './generated/amp-track-components.generated';
import { getKnownAmpTrackComponents, getProvisionalAmpTrackComponents } from './amp-track-component-registry';
import { AMP_TRACK_CONTEXT_VALUES, resolveAmpTrackContextFromUrl } from './amp-track-context-registry';

/**
 * GrowthBook feature that enables this QA console logger on deployed builds
 * (staging / private-cloud QA). Kept separate from `amplitude-tracking-flag`
 * so enabling real tracking in production SaaS does not also spam the console.
 * Defaults to `false`; local dev installs the logger regardless (see main.ts).
 */
export const AMPLITUDE_DEV_LOGGER_FLAG = 'amplitude-dev-logger-flag';

const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const KNOWN_CONTEXTS: ReadonlySet<string> = AMP_TRACK_CONTEXT_VALUES;

const ATTR = {
  context: 'data-amp-track-context',
  component: 'data-amp-track-component',
  detail: 'data-amp-track-detail',
} as const;

export type AmpAttrName = keyof typeof ATTR;

export interface AmpAttrs {
  context: string | null;
  component: string | null;
  detail: string | null;
}

export interface AmpValidationIssue {
  attr: AmpAttrName;
  level: 'error' | 'warn';
  message: string;
}

export type AmpValidationStatus = 'OK' | 'PROVISIONAL' | 'WARN' | 'INVALID';

export interface AmpValidationResult {
  status: AmpValidationStatus;
  issues: AmpValidationIssue[];
  /** True when the component value is recognised as provisional (pending approval). */
  provisional?: boolean;
}

export interface AmpValidationOptions {
  /** When set, component values not in the registry emit a warn-level issue. */
  knownComponents?: ReadonlySet<string>;
  /** When set, context values not in the registry emit a warn-level issue. */
  knownContexts?: ReadonlySet<string>;
  /**
   * When set, component values present in this set are flagged as
   * provisional (info-level only — they pass `isKnown` but are still
   * pending analytics-owner approval).
   */
  provisionalComponents?: ReadonlySet<string>;
  /** Registry allowedDetails map — warn when detail is outside the whitelist. */
  componentDetails?: Readonly<Record<string, readonly string[]>>;
  /** Legacy alias keys that should map to canonical component names. */
  componentAliases?: Readonly<Record<string, string>>;
  /** When set, compare effective DOM context with URL-resolved context. */
  urlResolvedContext?: string | null;
}

export interface AmpAuditOptions {
  /** Log per-element groups. Default true. Set false for summary-only (PM-friendly). */
  verbose?: boolean;
  /** When set, only log elements whose status is in this list (counts still reflect full scan). */
  statuses?: readonly AmpValidationStatus[];
}

export interface AmpNormalizedEvent {
  eventType: '[Amplitude] Element Clicked';
  eventProperties: {
    '[Amplitude] Element Tag': string;
    '[Amplitude] Element Text': string;
    'data-amp-track-context': string | null;
    'data-amp-track-component': string | null;
    'data-amp-track-detail': string | null;
  };
}

let lastAuditReport: AmpAuditReport | null = null;

export function getLastAmpAuditReport(): AmpAuditReport | null {
  return lastAuditReport;
}

export function getDefaultValidationOptions(overrides: Partial<AmpValidationOptions> = {}): AmpValidationOptions {
  return {
    knownComponents: getKnownAmpTrackComponents(),
    knownContexts: KNOWN_CONTEXTS,
    provisionalComponents: getProvisionalAmpTrackComponents(),
    componentDetails: AMP_TRACK_COMPONENT_DETAILS,
    componentAliases: AMP_TRACK_COMPONENT_ALIASES,
    ...overrides,
  };
}

export function readAmpAttrs(el: Element): AmpAttrs {
  return {
    context: el.getAttribute(ATTR.context),
    component: el.getAttribute(ATTR.component),
    detail: el.getAttribute(ATTR.detail),
  };
}

/** Resolve context from nearest ancestor when the element itself has none. */
export function readEffectiveAmpAttrs(el: Element): AmpAttrs {
  const direct = readAmpAttrs(el);
  if (direct.context) return direct;

  const ancestor = el.closest(`[${ATTR.context}]`);
  if (ancestor && ancestor !== el) {
    return { ...direct, context: ancestor.getAttribute(ATTR.context) };
  }

  return direct;
}

function checkKebab(attr: AmpAttrName, value: string): AmpValidationIssue | null {
  if (KEBAB_CASE.test(value)) return null;
  return {
    attr,
    level: 'error',
    message: `${ATTR[attr]}="${value}" is not kebab-case (lowercase letters/digits separated by single hyphens)`,
  };
}

function getAllowedDetails(
  component: string,
  options: AmpValidationOptions,
): readonly string[] | undefined {
  return options.componentDetails?.[component];
}

export function validateAmpAttrs(attrs: AmpAttrs, options: AmpValidationOptions = {}): AmpValidationResult {
  const issues: AmpValidationIssue[] = [];

  // component — required + (optional) registry membership check.
  if (attrs.component == null || attrs.component === '') {
    issues.push({
      attr: 'component',
      level: 'error',
      message: `${ATTR.component} is required (set ampTrack on the element)`,
    });
  } else {
    const issue = checkKebab('component', attrs.component);
    if (issue) {
      issues.push(issue);
    } else if (options.knownComponents && !options.knownComponents.has(attrs.component)) {
      const canonical = options.componentAliases?.[attrs.component];
      if (canonical) {
        issues.push({
          attr: 'component',
          level: 'warn',
          message: `${ATTR.component}="${attrs.component}" is an alias — use "${canonical}" instead`,
        });
      } else {
        issues.push({
          attr: 'component',
          level: 'warn',
          message: `${ATTR.component}="${attrs.component}" is not in the canonical registry`,
        });
      }
    }
  }

  // context — recommended (warn if missing, error if malformed, warn if unknown).
  if (attrs.context == null || attrs.context === '') {
    issues.push({
      attr: 'context',
      level: 'warn',
      message: `${ATTR.context} is missing — set ampTrackCtx on a parent or use provideAmpTrackCtx()`,
    });
  } else {
    const issue = checkKebab('context', attrs.context);
    if (issue) {
      issues.push(issue);
    } else if (options.knownContexts && !options.knownContexts.has(attrs.context)) {
      issues.push({
        attr: 'context',
        level: 'warn',
        message: `${ATTR.context}="${attrs.context}" is not in the canonical registry`,
      });
    } else if (
      options.urlResolvedContext
      && options.knownContexts?.has(options.urlResolvedContext)
      && attrs.context !== options.urlResolvedContext
    ) {
      issues.push({
        attr: 'context',
        level: 'warn',
        message:
          `${ATTR.context}="${attrs.context}" differs from URL-resolved "${options.urlResolvedContext}" — `
          + 'check provideAmpTrackCtx() or an intentional ampTrackContext override',
      });
    }
  }

  // detail — optional (kebab-case + allowedDetails when registry defines them)
  if (attrs.detail != null && attrs.detail !== '') {
    const issue = checkKebab('detail', attrs.detail);
    if (issue) {
      issues.push(issue);
    } else if (attrs.component) {
      const allowed = getAllowedDetails(attrs.component, options);
      if (allowed?.length && !allowed.includes(attrs.detail)) {
        issues.push({
          attr: 'detail',
          level: 'warn',
          message:
            `${ATTR.detail}="${attrs.detail}" is not in allowedDetails for ${attrs.component}: `
            + `[${allowed.join(', ')}]`,
        });
      }
    }
  }

  const provisional = !!(
    attrs.component
    && options.provisionalComponents
    && options.provisionalComponents.has(attrs.component)
  );

  const hasError = issues.some(i => i.level === 'error');
  const hasWarn = issues.some(i => i.level === 'warn');
  const status: AmpValidationStatus = hasError
    ? 'INVALID'
    : hasWarn
      ? 'WARN'
      : provisional
        ? 'PROVISIONAL'
        : 'OK';

  return { status, issues, provisional };
}

export function normalizeAmpEvent(el: Element, attrs: AmpAttrs): AmpNormalizedEvent {
  return {
    eventType: '[Amplitude] Element Clicked',
    eventProperties: {
      '[Amplitude] Element Tag': el.tagName.toLowerCase(),
      '[Amplitude] Element Text': (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 64),
      [ATTR.context]: attrs.context,
      [ATTR.component]: attrs.component,
      [ATTR.detail]: attrs.detail,
    },
  };
}

const STATUS_STYLES: Record<AmpValidationStatus, { color: string, bg: string }> = {
  OK:          { color: '#067647', bg: '#ecfdf3' },
  PROVISIONAL: { color: '#5925dc', bg: '#f4f3ff' },
  WARN:        { color: '#b54708', bg: '#fffaeb' },
  INVALID:     { color: '#b42318', bg: '#fef3f2' },
};

function badgeStyle(status: AmpValidationStatus): string {
  const { color, bg } = STATUS_STYLES[status];
  return `background:${bg};color:${color};font-weight:bold;padding:1px 6px;border-radius:4px;border:1px solid ${color}`;
}

const LABEL_STYLE = 'color:#4b3fb5;font-weight:bold';
const SECTION_STYLE = 'font-weight:bold;color:#101828';

function getCurrentPathname(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

function logAllowedDetailsHint(component: string, options: AmpValidationOptions): void {
  const allowed = getAllowedDetails(component, options);
  if (allowed?.length) {
    console.log('%cAllowed details for this component', SECTION_STYLE, allowed);
  }
}

/**
 * Log a single Amplitude-tracked interaction with a verifiable, color-coded
 * group containing both the raw DOM attributes and the normalized event.
 */
export function logAmpInteraction(
  el: Element,
  source: 'click' | 'audit' = 'click',
  options: AmpValidationOptions = getDefaultValidationOptions(),
): AmpValidationResult {
  const directAttrs = readAmpAttrs(el);
  const attrs = readEffectiveAmpAttrs(el);
  const validation = validateAmpAttrs(attrs, options);
  const event = normalizeAmpEvent(el, attrs);

  const summary = [
    attrs.component ?? '(no component)',
    attrs.context ?? '(no context)',
    attrs.detail,
  ].filter(Boolean).join(' · ');

  console.groupCollapsed(
    `%c[Amplitude]%c ${validation.status} %c${source}  ${summary}`,
    LABEL_STYLE,
    badgeStyle(validation.status),
    'color:#475467',
  );

  console.log('%cRaw DOM attributes', SECTION_STYLE);
  console.table({
    [ATTR.context]:   { value: directAttrs.context   ?? attrs.context ?? '(missing)' },
    [ATTR.component]: { value: directAttrs.component ?? '(missing)' },
    [ATTR.detail]:    { value: directAttrs.detail    ?? '(none — optional)' },
    ...(directAttrs.context ? {} : attrs.context ? { 'effective context (inherited)': { value: attrs.context } } : {}),
  });

  console.log('%cNormalized Amplitude event', SECTION_STYLE, event);

  if (validation.provisional) {
    console.log(
      `%cPROVISIONAL%c "${attrs.component}" is in active use but pending analytics-owner approval. ` +
      'See provisional flag in registry/amplitude-registry.json (yarn sync:amp-registry).',
      badgeStyle('PROVISIONAL'),
      'color:#475467',
    );
  }

  if (validation.issues.length === 0) {
    console.log('%cAll naming conventions valid (kebab-case, required attrs present).', 'color:#067647');
  } else {
    console.log('%cValidation issues', `${SECTION_STYLE};color:${STATUS_STYLES[validation.status].color}`);
    for (const issue of validation.issues) {
      const fn = issue.level === 'error' ? console.error : console.warn;
      fn(`[${issue.attr}] ${issue.message}`);
    }
    if (attrs.component && validation.issues.some(i => i.attr === 'detail')) {
      logAllowedDetailsHint(attrs.component, options);
    }
  }

  console.log('%cElement', SECTION_STYLE, el);
  console.groupEnd();

  return validation;
}

export interface AmpAuditRow {
  index: number;
  status: AmpValidationStatus;
  context: string;
  component: string;
  detail: string;
  issues: number;
  messages: string[];
}

export interface AmpAuditReport {
  path: string;
  scannedAt: string;
  total: number;
  ok: number;
  provisional: number;
  warn: number;
  invalid: number;
  rows: AmpAuditRow[];
}

function shouldLogAuditRow(status: AmpValidationStatus, options: AmpAuditOptions): boolean {
  if (options.verbose === false) return false;
  if (!options.statuses?.length) return true;
  return options.statuses.includes(status);
}

/**
 * Scan the DOM for every element that exposes `data-amp-track-component`
 * and print a summary table plus optional per-element details.
 */
export function auditAmpAttrsOnPage(
  root: ParentNode = document,
  auditOptions: AmpAuditOptions = {},
): AmpAuditReport {
  const elements = Array.from(root.querySelectorAll<HTMLElement>(`[${ATTR.component}]`));
  const path = getCurrentPathname();
  const validationOptions = getDefaultValidationOptions({
    urlResolvedContext: resolveAmpTrackContextFromUrl(path),
  });

  const rows: AmpAuditRow[] = [];
  let ok = 0;
  let provisional = 0;
  let warn = 0;
  let invalid = 0;

  if (auditOptions.verbose !== false) {
    console.groupCollapsed(
      `%c[Amplitude] Page audit — ${elements.length} tracked element(s) on ${path}`,
      LABEL_STYLE,
    );
  }

  elements.forEach((el, index) => {
    const attrs = readEffectiveAmpAttrs(el);
    const validation = validateAmpAttrs(attrs, validationOptions);

    if (validation.status === 'OK') ok++;
    else if (validation.status === 'PROVISIONAL') provisional++;
    else if (validation.status === 'WARN') warn++;
    else invalid++;

    rows.push({
      index,
      status: validation.status,
      context: attrs.context ?? '',
      component: attrs.component ?? '',
      detail: attrs.detail ?? '',
      issues: validation.issues.length,
      messages: validation.issues.map(i => i.message),
    });

    if (shouldLogAuditRow(validation.status, auditOptions)) {
      logAmpInteraction(el, 'audit', validationOptions);
    }
  });

  if (auditOptions.verbose !== false) {
    console.groupEnd();
  }

  const summary
    = `%c[Amplitude] Audit summary — ${path} — `
    + `%cOK ${ok}%c · %cPROVISIONAL ${provisional}%c · `
    + `%cWARN ${warn}%c · %cINVALID ${invalid}%c · TOTAL ${elements.length}`;
  console.log(
    summary,
    LABEL_STYLE,
    `color:${STATUS_STYLES.OK.color};font-weight:bold`, '',
    `color:${STATUS_STYLES.PROVISIONAL.color};font-weight:bold`, '',
    `color:${STATUS_STYLES.WARN.color};font-weight:bold`, '',
    `color:${STATUS_STYLES.INVALID.color};font-weight:bold`, '',
  );

  if (rows.length) {
    const tableRows = auditOptions.statuses?.length
      ? rows.filter(r => auditOptions.statuses.includes(r.status))
      : rows;
    if (tableRows.length) console.table(tableRows);
  }

  if (warn + invalid > 0) {
    console.log(
      '%cTip for PMs:%c run %cwindow.__ampTrackAudit({ verbose: false, statuses: ["WARN","INVALID"] })%c '
      + 'for a compact issues-only view, or %cwindow.__ampTrackAuditCopy()%c to copy JSON.',
      'color:#475467', '',
      'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
      'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
    );
  }

  const report: AmpAuditReport = {
    path,
    scannedAt: new Date().toISOString(),
    total: elements.length,
    ok,
    provisional,
    warn,
    invalid,
    rows,
  };
  lastAuditReport = report;
  return report;
}

export function exportAmpAuditReport(): string | null {
  if (!lastAuditReport) return null;
  return JSON.stringify(lastAuditReport, null, 2);
}

export async function copyAmpAuditReportToClipboard(): Promise<boolean> {
  const json = exportAmpAuditReport();
  if (!json || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return false;
  }
  await navigator.clipboard.writeText(json);
  console.log('%c[Amplitude] Audit report copied to clipboard.', LABEL_STYLE);
  return true;
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __ampTrackAudit?: (root?: ParentNode, options?: AmpAuditOptions) => AmpAuditReport;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __ampTrackAuditExport?: () => string | null;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __ampTrackAuditCopy?: () => Promise<boolean>;
  }
}

let installedHandler: ((e: Event) => void) | null = null;
let installedTarget: Document | null = null;

function teardownAmpDevLogger(): void {
  if (installedHandler && installedTarget) {
    installedTarget.removeEventListener('click', installedHandler, true);
  }
  installedHandler = null;
  installedTarget = null;
  if (typeof window !== 'undefined') {
    delete window.__ampTrackAudit;
    delete window.__ampTrackAuditExport;
    delete window.__ampTrackAuditCopy;
  }
}

/**
 * Install a global click listener that logs every Amplitude-tracked
 * interaction, plus exposes audit helpers for manual QA.
 *
 * Returns a teardown function. Idempotent — repeat calls are no-ops.
 */
export function installAmpDevLogger(target: Document = document): () => void {
  if (installedHandler) {
    return teardownAmpDevLogger;
  }

  let lastEl: Element | null = null;
  let lastTime = 0;

  const handler = (e: Event) => {
    const node = e.target as Element | null;
    const el = node?.closest?.(`[${ATTR.component}]`);
    if (!el) return;
    const now = Date.now();
    if (el === lastEl && now - lastTime < 100) return;
    lastEl = el;
    lastTime = now;
    logAmpInteraction(el, 'click', getDefaultValidationOptions({
      urlResolvedContext: resolveAmpTrackContextFromUrl(getCurrentPathname()),
    }));
  };

  installedHandler = handler;
  installedTarget = target;
  target.addEventListener('click', handler, true);
  if (typeof window !== 'undefined') {
    window.__ampTrackAudit = (root?: ParentNode, options?: AmpAuditOptions) =>
      auditAmpAttrsOnPage(root ?? target, options);
    window.__ampTrackAuditExport = exportAmpAuditReport;
    window.__ampTrackAuditCopy = copyAmpAuditReportToClipboard;
  }

  console.log(
    '%c[Amplitude] dev logger active',
    LABEL_STYLE,
  );
  console.log(
    '%cPM quick reference:%c\n'
    + '  • Click any tracked element to inspect its payload\n'
    + '  • %cwindow.__ampTrackAudit()%c — full page scan\n'
    + '  • %cwindow.__ampTrackAudit({ verbose: false })%c — summary table only\n'
    + '  • %cwindow.__ampTrackAudit({ statuses: ["WARN","INVALID"] })%c — problems only\n'
    + '  • %cwindow.__ampTrackAuditCopy()%c — copy last audit as JSON',
    'color:#475467', '',
    'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
    'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
    'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
    'font-family:monospace;background:#f2f4f7;padding:1px 4px;border-radius:3px', '',
  );

  return teardownAmpDevLogger;
}
